import { api } from "../api";

// ── Types ────────────────────────────────────────────────────

export interface Vendor {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  contactPerson?: string | null;
  currency: string;
  taxRate?: number | null;
  discountRate?: number | null;
  avgDeliveryDays?: number | null;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VendorDetail extends Vendor {
  purchaseOrders: { id: string; orderNumber: string; status: string; createdAt: string }[];
  _count: { purchaseOrders: number; invoices: number };
}

export interface Fund {
  id: string;
  tenantId: string;
  branchId?: string | null;
  name: string;
  code: string;
  description?: string | null;
  fiscalYear: number;
  allocation: number;
  encumbered: number;
  expended: number;
  available: number;
  utilizationPercent: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  branch?: { id: string; name: string; code: string } | null;
  _count?: { transactions: number; poLines: number; requests: number };
}

export interface BudgetTransaction {
  id: string;
  fundId: string;
  type: string;
  amount: number;
  description?: string | null;
  poLineId?: string | null;
  invoiceLineId?: string | null;
  createdAt: string;
}

export interface FundDetail extends Fund {
  transactions: BudgetTransaction[];
}

export interface BudgetSummary {
  totalAllocation: number;
  totalEncumbered: number;
  totalExpended: number;
  totalAvailable: number;
  utilizationPercent: number;
  fundCount: number;
}

export interface AcquisitionRequest {
  id: string;
  tenantId: string;
  requesterId: string;
  reviewerId?: string | null;
  source: "PATRON" | "STAFF";
  status: string;
  urgency: "LOW" | "MEDIUM" | "HIGH";
  title: string;
  subtitle?: string | null;
  description?: string | null;
  authors?: string[];
  editors?: string[];
  /** Joined authors, kept for screens that show a single line. */
  author?: string | null;
  isbn?: string | null;
  issn?: string | null;
  publisher?: string | null;
  placeOfPublication?: string | null;
  publicationYear?: number | null;
  edition?: string | null;
  format?: string | null;
  quantity: number;
  estimatedCost?: number | null;
  fundId?: string | null;
  preferredVendorId?: string | null;
  justification?: string | null;
  rejectionReason?: string | null;
  autoApproved: boolean;
  createdAt: string;
  updatedAt: string;
  fund?: { id: string; name: string; code: string } | null;
  preferredVendor?: { id: string; name: string; code: string } | null;
}

export interface AcqRequestDetail extends AcquisitionRequest {
  timeline: TimelineEvent[];
  poLines: {
    id: string;
    title: string;
    purchaseOrder: { id: string; orderNumber: string; status: string; vendorId: string } | null;
  }[];
}

export interface TimelineEvent {
  id: string;
  requestId: string;
  event: string;
  actorId?: string | null;
  data?: Record<string, unknown> | null;
  createdAt: string;
}

export interface PurchaseOrder {
  id: string;
  tenantId: string;
  vendorId: string;
  orderNumber: string;
  status: string;
  notes?: string | null;
  sentAt?: string | null;
  createdAt: string;
  updatedAt: string;
  vendor: { id: string; name: string; code: string };
  _count?: { lines: number; shipments: number };
}

export interface POLine {
  id: string;
  purchaseOrderId: string;
  acquisitionRequestId?: string | null;
  fundId?: string | null;
  title: string;
  isbn?: string | null;
  quantity: number;
  unitPrice: number;
  quantityReceived: number;
  /** Units turned into catalog records for this line (supports partial receive → catalog → receive more). */
  catalogedQuantity?: number;
  biblioId?: string | null;
  catalogAtReceive: boolean;
  deferCatalogEnrichment?: boolean;
  fund?: { id: string; name: string; code: string } | null;
  acquisitionRequest?: { id: string; title: string; status: string } | null;
}

export interface PurchaseOrderDetail extends Omit<PurchaseOrder, "_count"> {
  vendor: Vendor;
  lines: POLine[];
  shipments: {
    id: string;
    receivedById: string;
    receivedAt: string;
    notes?: string | null;
    lines: {
      id: string;
      poLineId: string;
      quantityReceived: number;
      conditionNotes?: string | null;
    }[];
  }[];
  invoices: { id: string; invoiceNumber: string; status: string; totalAmount: number }[];
}

export interface AcqInvoice {
  id: string;
  tenantId: string;
  purchaseOrderId?: string | null;
  vendorId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string | null;
  totalAmount: number;
  currency: string;
  status: string;
  notes?: string | null;
  createdAt: string;
  vendor: { id: string; name: string; code: string };
  purchaseOrder?: { id: string; orderNumber: string } | null;
  _count?: { lines: number };
}

export interface AcqInvoiceDetail extends AcqInvoice {
  lines: {
    id: string;
    poLineId?: string | null;
    description?: string | null;
    quantity: number;
    unitPrice: number;
    poLine?: {
      id: string;
      title: string;
      isbn?: string | null;
      quantity: number;
      unitPrice: number;
    } | null;
  }[];
}

export interface DuplicateCheckResult {
  existingBiblios: { id: string; title: string; isbn?: string | null; author?: string | null }[];
  openRequests: { id: string; title: string; isbn?: string | null; status: string }[];
}

export interface DashboardStats {
  openRequests: number;
  pendingOrders: number;
  awaitingReceipt: number;
  budgetUtilization: number;
  totalBudget: number;
  totalUsed: number;
}

export interface PaginatedResponse<T> {
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
}

export interface VendorListResponse extends PaginatedResponse<Vendor> {
  vendors: Vendor[];
}

export interface FundListResponse extends PaginatedResponse<Fund> {
  funds: Fund[];
}

export interface RequestListResponse extends PaginatedResponse<AcquisitionRequest> {
  requests: AcquisitionRequest[];
}

export interface OrderListResponse extends PaginatedResponse<PurchaseOrder> {
  orders: PurchaseOrder[];
}

export interface AcqInvoiceListResponse extends PaginatedResponse<AcqInvoice> {
  invoices: AcqInvoice[];
}

// ── API ──────────────────────────────────────────────────────

export const acquisitionsApi = {
  // Vendors
  vendors: {
    list: (params?: {
      search?: string;
      isActive?: boolean;
      limit?: number;
      cursor?: string;
      sortBy?: "name" | "code" | "createdAt";
      sortOrder?: "asc" | "desc";
    }) => api.get<VendorListResponse>("/acquisitions/vendors", { params }).then((r) => r.data),

    getById: (id: string) =>
      api.get<VendorDetail>(`/acquisitions/vendors/${id}`).then((r) => r.data),

    create: (data: {
      name: string;
      code: string;
      address?: string;
      phone?: string;
      email?: string;
      website?: string;
      contactPerson?: string;
      currency?: string;
      taxRate?: number;
      discountRate?: number;
      avgDeliveryDays?: number;
      notes?: string;
      isActive?: boolean;
    }) => api.post<Vendor>("/acquisitions/vendors", data).then((r) => r.data),

    update: (
      id: string,
      data: Partial<{
        name: string;
        code: string;
        address?: string;
        phone?: string;
        email?: string;
        website?: string;
        contactPerson?: string;
        currency?: string;
        taxRate?: number;
        discountRate?: number;
        avgDeliveryDays?: number;
        notes?: string;
        isActive?: boolean;
      }>
    ) => api.patch<Vendor>(`/acquisitions/vendors/${id}`, data).then((r) => r.data),
  },

  // Funds / Budget
  funds: {
    list: (params?: {
      search?: string;
      isActive?: boolean;
      fiscalYear?: number;
      branchId?: string;
      limit?: number;
      cursor?: string;
    }) => api.get<FundListResponse>("/acquisitions/funds", { params }).then((r) => r.data),

    getById: (id: string) => api.get<FundDetail>(`/acquisitions/funds/${id}`).then((r) => r.data),

    summary: (fiscalYear?: number) =>
      api
        .get<BudgetSummary>("/acquisitions/funds/summary", {
          params: fiscalYear ? { fiscalYear } : undefined,
        })
        .then((r) => r.data),

    create: (data: {
      name: string;
      code: string;
      description?: string;
      fiscalYear: number;
      allocation: number;
      branchId?: string;
      isActive?: boolean;
    }) => api.post<Fund>("/acquisitions/funds", data).then((r) => r.data),

    update: (
      id: string,
      data: Partial<{
        name: string;
        code: string;
        description?: string;
        fiscalYear: number;
        allocation: number;
        branchId?: string;
        isActive?: boolean;
      }>
    ) => api.patch<Fund>(`/acquisitions/funds/${id}`, data).then((r) => r.data),

    transfer: (data: { fromFundId: string; toFundId: string; amount: number; reason?: string }) =>
      api
        .post<{ success: boolean; message: string }>("/acquisitions/funds/transfer", data)
        .then((r) => r.data),
  },

  /** FR-BUD-06 tenant threshold + warning list */
  budgetSettings: {
    get: () =>
      api
        .get<{
          fundUtilizationWarningPercent: number | null;
          acquisitionAutoApproveThreshold: number | null;
        }>("/acquisitions/budget/settings")
        .then((r) => r.data),

    patch: (data: {
      fundUtilizationWarningPercent?: number;
      disableFundUtilizationWarning?: boolean;
      acquisitionAutoApproveThreshold?: number;
      disableAutoApproval?: boolean;
    }) =>
      api
        .patch<{
          fundUtilizationWarningPercent: number | null;
          acquisitionAutoApproveThreshold: number | null;
        }>("/acquisitions/budget/settings", data)
        .then((r) => r.data),

    fundWarnings: () =>
      api
        .get<{
          threshold: number | null;
          funds: Array<{
            id: string;
            name: string;
            code: string;
            fiscalYear: number;
            allocation: number;
            encumbered: number;
            expended: number;
            utilizationPercent: number;
          }>;
        }>("/acquisitions/budget/fund-warnings")
        .then((r) => r.data),
  },

  // Requests
  requests: {
    list: (params?: {
      search?: string;
      status?: string;
      source?: string;
      urgency?: string;
      fundId?: string;
      /** ISO date YYYY-MM-DD (UTC day bounds) */
      createdFrom?: string;
      createdTo?: string;
      limit?: number;
      cursor?: string;
    }) => api.get<RequestListResponse>("/acquisitions/requests", { params }).then((r) => r.data),

    listMy: (params?: { limit?: number; cursor?: string }) =>
      api
        .get<{
          requests: AcquisitionRequest[];
          nextCursor: string | null;
          hasMore: boolean;
        }>("/acquisitions/requests/my", { params })
        .then((r) => r.data),

    getById: (id: string) =>
      api.get<AcqRequestDetail>(`/acquisitions/requests/${id}`).then((r) => r.data),

    create: (data: {
      title: string;
      subtitle?: string | null;
      description?: string | null;
      authors?: string[];
      author?: string;
      editors?: string[];
      isbn?: string | null;
      issn?: string | null;
      publisher?: string | null;
      placeOfPublication?: string | null;
      publicationYear?: number | null;
      edition?: string | null;
      format?: string | null;
      quantity?: number;
      estimatedCost?: number | null;
      fundId?: string | null;
      justification?: string | null;
      urgency?: string;
    }) => api.post<AcquisitionRequest>("/acquisitions/requests", data).then((r) => r.data),

    update: (
      id: string,
      data: Partial<{
        title: string;
        subtitle: string | null;
        description: string | null;
        authors: string[];
        author: string | null;
        editors: string[];
        isbn: string | null;
        issn: string | null;
        publisher: string | null;
        placeOfPublication: string | null;
        publicationYear: number | null;
        edition: string | null;
        format: string | null;
        quantity: number;
        estimatedCost: number | null;
        fundId: string | null;
        justification: string | null;
        urgency: string;
      }>
    ) => api.patch<AcquisitionRequest>(`/acquisitions/requests/${id}`, data).then((r) => r.data),

    delete: (id: string) =>
      api.delete<{ deleted: boolean }>(`/acquisitions/requests/${id}`).then((r) => r.data),

    convertPatron: (
      id: string,
      data: {
        fundId: string;
        estimatedCost: number;
        quantity: number;
        preferredVendorId?: string;
        note?: string;
      }
    ) =>
      api
        .post<AcqRequestDetail>(`/acquisitions/requests/${id}/convert-patron`, data)
        .then((r) => r.data),

    submit: (id: string) =>
      api.post<AcquisitionRequest>(`/acquisitions/requests/${id}/submit`).then((r) => r.data),

    startReview: (id: string) =>
      api.post<AcquisitionRequest>(`/acquisitions/requests/${id}/start-review`).then((r) => r.data),

    approve: (id: string, note?: string) =>
      api
        .post<AcquisitionRequest>(`/acquisitions/requests/${id}/approve`, { note })
        .then((r) => r.data),

    reject: (id: string, reason: string) =>
      api
        .post<AcquisitionRequest>(`/acquisitions/requests/${id}/reject`, { reason })
        .then((r) => r.data),

    requestInfo: (id: string, question: string) =>
      api
        .post<AcquisitionRequest>(`/acquisitions/requests/${id}/request-info`, { question })
        .then((r) => r.data),

    bulkApprove: (ids: string[], note?: string) =>
      api
        .post<{
          approved: string[];
          failed: { id: string; reason: string }[];
          total: number;
        }>("/acquisitions/requests/bulk-approve", { ids, note })
        .then((r) => r.data),

    checkDuplicates: (isbn?: string, title?: string) =>
      api
        .get<DuplicateCheckResult>("/acquisitions/requests/check-duplicates", {
          params: { isbn, title },
        })
        .then((r) => r.data),
  },

  // Orders
  orders: {
    list: (params?: {
      search?: string;
      status?: string;
      vendorId?: string;
      limit?: number;
      cursor?: string;
    }) => api.get<OrderListResponse>("/acquisitions/orders", { params }).then((r) => r.data),

    getById: (id: string) =>
      api.get<PurchaseOrderDetail>(`/acquisitions/orders/${id}`).then((r) => r.data),

    create: (data: {
      vendorId: string;
      notes?: string;
      lines: {
        acquisitionRequestId?: string;
        fundId?: string;
        title: string;
        isbn?: string;
        quantity: number;
        unitPrice: number;
        catalogAtReceive?: boolean;
        deferCatalogEnrichment?: boolean;
      }[];
    }) => api.post<PurchaseOrderDetail>("/acquisitions/orders", data).then((r) => r.data),

    send: (id: string) =>
      api.post<PurchaseOrder>(`/acquisitions/orders/${id}/send`).then((r) => r.data),

    cancel: (id: string) =>
      api.post<PurchaseOrder>(`/acquisitions/orders/${id}/cancel`).then((r) => r.data),

    receive: (
      id: string,
      data: {
        notes?: string;
        lines: { poLineId: string; quantityReceived: number; conditionNotes?: string }[];
      }
    ) => api.post(`/acquisitions/orders/${id}/receive`, data).then((r) => r.data),

    catalog: (
      id: string,
      data?: {
        lineIds?: string[];
        itemDefaults?: {
          itemTypeId?: string;
          locationId?: string;
          collectionId?: string;
          branchId?: string;
          conditionCode?: string;
          statusCode?: string;
        };
      }
    ) =>
      api
        .post<{
          cataloged: {
            poLineId: string;
            title: string;
            biblioId: string;
            itemCount: number;
            deferred: boolean;
          }[];
          failed: {
            poLineId: string;
            title: string;
            biblioId: string;
            itemCount: number;
            error?: string;
          }[];
          orderClosed: boolean;
        }>(`/acquisitions/orders/${id}/catalog`, data || {})
        .then((r) => r.data),
  },

  // Acquisition Invoices
  acqInvoices: {
    list: (params?: {
      search?: string;
      status?: string;
      vendorId?: string;
      limit?: number;
      cursor?: string;
    }) => api.get<AcqInvoiceListResponse>("/acquisitions/invoices", { params }).then((r) => r.data),

    getById: (id: string) =>
      api.get<AcqInvoiceDetail>(`/acquisitions/invoices/${id}`).then((r) => r.data),

    create: (data: {
      vendorId: string;
      purchaseOrderId?: string;
      invoiceNumber: string;
      invoiceDate: string;
      dueDate?: string;
      totalAmount: number;
      currency?: string;
      notes?: string;
      lines?: { poLineId?: string; description?: string; quantity: number; unitPrice: number }[];
    }) => api.post<AcqInvoiceDetail>("/acquisitions/invoices", data).then((r) => r.data),

    match: (id: string) =>
      api.post<AcqInvoiceDetail>(`/acquisitions/invoices/${id}/match`).then((r) => r.data),

    reverseMatch: (id: string) =>
      api.post<AcqInvoiceDetail>(`/acquisitions/invoices/${id}/reverse-match`).then((r) => r.data),

    markPaid: (id: string) =>
      api.post<AcqInvoiceDetail>(`/acquisitions/invoices/${id}/mark-paid`).then((r) => r.data),

    cancelDraft: (id: string) =>
      api.post<AcqInvoiceDetail>(`/acquisitions/invoices/${id}/cancel`).then((r) => r.data),
  },

  // Dashboard
  dashboard: {
    stats: () => api.get<DashboardStats>("/acquisitions/dashboard/stats").then((r) => r.data),
  },

  reports: {
    fundUtilization: (params?: { fiscalYear?: number }) =>
      api
        .get<{
          funds: Array<{
            id: string;
            name: string;
            code: string;
            fiscalYear: number;
            allocation: number;
            encumbered: number;
            expended: number;
            available: number;
            utilizationPercent: number;
          }>;
        }>("/acquisitions/reports/fund-utilization", { params })
        .then((r) => r.data),

    vendorSpend: () =>
      api
        .get<{
          vendors: Array<{
            vendorId: string;
            name: string;
            code: string;
            lineCount: number;
            totalSpend: number;
          }>;
        }>("/acquisitions/reports/vendor-spend")
        .then((r) => r.data),

    auditLog: (params?: { limit?: number }) =>
      api
        .get<
          Array<{
            id: string;
            action: string;
            resourceType: string;
            resourceId: string | null;
            description: string | null;
            createdAt: string;
            user?: { email: string | null } | null;
          }>
        >("/acquisitions/reports/audit-log", { params })
        .then((r) => r.data),
  },
};
